import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRightLeft } from 'lucide-react';
import { fetchApi } from '../config/api';
import { Alert, Badge, EmptyState, Loading, PageBackdrop, PageHeader } from '../components/UI';
import { formatDate } from '../utils/formatDate';
import { breedNameFor, DogTransferItem } from '../utils/dogTransfer';

const STATUS_VARIANT: Record<DogTransferItem['status'], 'warning' | 'success' | 'secondary' | 'error'> = {
  pending: 'warning',
  completed: 'success',
  cancelled: 'secondary',
  expired: 'secondary'
};

/**
 * Die getrennte Liste des Zuechters: Hunde, die er uebergeben hat oder gerade uebergibt. Nach der Uebergabe
 * stehen hier nur noch die Stammdaten zum Zeitpunkt der Uebergabe - der Hund gehoert dem Kaeufer.
 */
const DogTransfers: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [items, setItems] = useState<DogTransferItem[] | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchApi('/dog-transfers/outgoing')
      .then((res) => setItems(Array.isArray(res) ? res : []))
      .catch((err: any) => setError(err.message || t('transfer:list.loadError')));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const open = items?.filter((i) => i.status === 'pending') ?? [];
  const done = items?.filter((i) => i.status === 'completed') ?? [];
  const other = items?.filter((i) => i.status === 'cancelled' || i.status === 'expired') ?? [];

  const whenText = (item: DogTransferItem) => {
    switch (item.status) {
      case 'completed':
        return t('transfer:list.handedOverTo', { name: item.toUserName ?? '', date: item.completedAt ? formatDate(item.completedAt) : '' });
      case 'pending':
        return `${t('transfer:list.createdOn', { date: formatDate(item.createdAt) })} · ${t('transfer:list.expiresOn', { date: formatDate(item.expiresAt) })}`;
      case 'cancelled':
        return t('transfer:list.cancelledOn', { date: item.cancelledAt ? formatDate(item.cancelledAt) : '' });
      default:
        return t('transfer:list.expiredOn', { date: formatDate(item.expiresAt) });
    }
  };

  const renderItem = (item: DogTransferItem) => {
    const details = [
      breedNameFor(item, i18n.language),
      t(`transfer:sex.${item.sex}`, { defaultValue: item.sex }),
      item.dateOfBirth ? formatDate(item.dateOfBirth) : null,
      item.microchipNumber ? t('transfer:list.microchip', { number: item.microchipNumber }) : null
    ].filter(Boolean);

    return (
      <div key={item.id} className="card space-y-1">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="text-lg font-semibold text-dark-50">{item.dogName}</div>
          <Badge variant={STATUS_VARIANT[item.status]} size="sm">{t(`transfer:status.${item.status}`)}</Badge>
        </div>
        <div className="text-sm text-dark-300">{details.join(' · ')}</div>
        <div className="text-sm text-dark-400">{whenText(item)}</div>
        {item.note && <div className="text-sm text-dark-300">{item.note}</div>}
        {item.status === 'pending' && item.code && (
          <div className="font-mono text-xl font-bold uppercase tracking-widest text-dark-50">{item.code}</div>
        )}
        {item.dogId && item.status !== 'completed' && (
          <Link to={`/dogs/${item.dogId}`} className="inline-block text-sm text-primary-300 hover:underline">
            {t('transfer:list.openDog')}
          </Link>
        )}
      </div>
    );
  };

  const section = (heading: string, list: DogTransferItem[]) =>
    list.length > 0 && (
      <section className="space-y-3">
        <h2 className="text-lg font-semibold text-dark-100">{heading}</h2>
        <div className="grid gap-3 md:grid-cols-2">{list.map(renderItem)}</div>
      </section>
    );

  return (
    <div className="relative min-h-screen overflow-x-hidden">
      <PageBackdrop />

      <div className="relative z-10 container mx-auto space-y-6 px-4 py-8 sm:px-6 sm:py-10">
        <PageHeader
          title={t('transfer:list.title')}
          subtitle={t('transfer:list.subtitle')}
          backLink={{ label: t('nav:primary.cockpit'), onClick: () => navigate('/dashboard') }}
          actions={
            <Link to="/hund-uebernehmen" className="btn btn-ghost">
              <ArrowRightLeft size={18} />
              {t('transfer:list.takeOver')}
            </Link>
          }
        />

        {error && <Alert type="error" message={error} />}
        {items === null && !error && <Loading />}
        {items?.length === 0 && <EmptyState icon="🐾" title={t('transfer:list.title')} description={t('transfer:list.empty')} />}

        {section(t('transfer:list.openHeading'), open)}
        {section(t('transfer:list.doneHeading'), done)}
        {done.length > 0 && <p className="text-sm text-dark-400">{t('transfer:list.snapshotNote')}</p>}
        {section(t('transfer:list.otherHeading'), other)}
      </div>
    </div>
  );
};

export default DogTransfers;
